/**
 * i18n setup — bilingual (English + Arabic) with RTL toggle.
 *
 * To roll back the entire feature: delete this folder, remove the
 * import from main.tsx, and remove the language switcher button.
 * No other code changes needed — useTranslation falls back gracefully.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

export function isRtl(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang.split('-')[0] as SupportedLanguage);
}

/**
 * The language served from unprefixed urls. English keeps `/pricing` so the
 * urls that already rank stay where they are; every other language gets a
 * prefix of its own.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/** Url prefix a language's public pages live under, '' for the default one. */
export function localePrefix(lang: string): string {
  const resolved = lang.split('-')[0];
  return resolved === DEFAULT_LANGUAGE || !SUPPORTED_LANGUAGES.includes(resolved as SupportedLanguage)
    ? ''
    : `/${resolved}`;
}

/**
 * Splits a pathname into the language its prefix names and the path beneath it:
 * `/ar/pricing` -> `{ language: 'ar', path: '/pricing' }`, and
 * `/pricing` -> `{ language: 'en', path: '/pricing' }`.
 */
export function splitLocalePath(pathname: string): {
  language: SupportedLanguage;
  path: string;
} {
  for (const lang of SUPPORTED_LANGUAGES) {
    if (lang === DEFAULT_LANGUAGE) continue;
    if (pathname === `/${lang}` || pathname.startsWith(`/${lang}/`)) {
      return { language: lang, path: pathname.slice(lang.length + 1) || '/' };
    }
  }
  return { language: DEFAULT_LANGUAGE, path: pathname || '/' };
}

/** Builds the url a path has in a given language. */
export function localePath(path: string, lang: string): string {
  const prefix = localePrefix(lang);
  if (!prefix) return path;
  return path === '/' ? prefix : `${prefix}${path}`;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // Browser language detection commonly returns regional tags such as ar-SA.
    // We only ship language-level resources, so resolve those tags to `ar`
    // instead of falling through to English.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ryze_lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

// Keep <html dir> and <html lang> in sync with the active language
function applyDirection(lang: string): void {
  const resolvedLang = lang.split('-')[0];
  const dir = isRtl(resolvedLang) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir',  dir);
  document.documentElement.setAttribute('lang', resolvedLang);
}

if (typeof document !== 'undefined') {
  applyDirection(i18n.language);
  i18n.on('languageChanged', applyDirection);
}

export default i18n;
